<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-0"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon

            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Username -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Username"
                  data-vv-validate-on="blur"
                  rules="required|alpha-num"
                >
                  <b-form-group

                    :label="$t('DEBTIA_USERNAME')"
                    label-for="username"
                  >
                    <b-form-input
                      id="username"
                      v-model="useUserData.username"
                      :state="getValidationState(validationContext)"
                      trim
                      :maxlength="maxCharactersAllowed"
                      @input="characterLeft"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                    <div>
                      <small
                        :style="maxCharacters > 10 ? 'color: green' : 'color: red'"
                      >
                        {{ maxCharacters }} characters left out of {{ maxCharactersAllowed }}
                      </small>
                    </div>
                    <span
                      v-if="!validateUsername"
                      style="color:#ea5455"
                    >
                      Username is not valid
                    </span>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="useUserData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="useUserData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="useUserData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_LOGIN')"
                  label-for="login-status"
                >
                  <v-select
                    v-model="useUserData.loginStatus"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="login-status"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Dropdown -->
              <b-col
                v-if="!debtorsBelongToCompany"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_SELECT_CLIENT')"
                  label-for="select-client"
                >
                  <v-select
                    v-model="useUserData.client_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="clients"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="select-client"
                    :value="'Select Client'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Contact Person -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CONTACT_PERSON')"
                  label-for="contact-person"
                >
                  <b-form-input
                    id="contact-person"
                    v-model="useUserData.contact_person"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Type -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CLIENT_TYPE')"
                  label-for="client-type"
                >
                  <v-select
                    v-model="useUserData.debtor_type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="client-type"
                    :value="'private-person'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: CVRCPR -->
              <b-col
                v-if="useUserData.debtor_type === 'capital-company' || useUserData.debtor_type === 'sole-proprietorship'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="CVR"
                  :rules="useUserData.address.country_id === 247 ? 'integer|length:8' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CVRCPR')"
                    label-for="cvrcpr"
                  >
                    <b-form-input
                      id="cvrcpr"
                      v-model="useUserData.cvr_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>

              <!-- Field: CPR -->
              <b-col
                v-if="useUserData.debtor_type === 'person'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CPR')"
                  label-for="cpr"
                >
                  <cleave
                    id="cpr"
                    v-model="useUserData.cpr"
                    class="form-control"
                    :raw="false"
                    :options="options.cprDelimiter"
                  />
                </b-form-group>

              </b-col>

            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_BANK_INFORMATION') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_BRANCH_NO')"
                  label-for="bankBranchNo"
                >
                  <b-form-input
                    id="bankBranchNo"
                    v-model="useUserData.bankBranchNo"
                    trim
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_ACCOUNT_NO')"
                  label-for="bankAccountNo"
                >
                  <b-form-input
                    id="bankAccountNo"
                    v-model="useUserData.bankAccountNo"
                    trim
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="useUserData.address.house_number"
                      :state="getValidationState(validationContext)"
                      :disabled="useUserData.hide_address"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="useUserData.address.road_name"
                      :state="getValidationState(validationContext)"
                      :disabled="useUserData.hide_address"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="useUserData.address.place"
                    :disabled="useUserData.hide_address"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <b-form-input
                      id="zip-code"
                      v-model="useUserData.address.zip_code"
                      :state="getValidationState(validationContext)"
                      :disabled="useUserData.hide_address"
                      trim
                      @change="getCityNCourtName($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="city"
                  >
                    <b-form-input
                      id="city"
                      v-model="useUserData.address.city"
                      :state="getValidationState(validationContext)"
                      :disabled="useUserData.hide_address"
                      trim
                      @change="getZipCode($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  v-slot="{ errors, validated, failed }"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COUNTRY')"
                    label-for="select-country"
                    :state="failed ? false : (validated ? true : null)"
                  >
                    <v-select
                      id="select-country"
                      v-model="useUserData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="true"
                      placeholder="Select Country"
                      :class="{ 'is-invalid': errors.length > 0 }"
                      @input="onCountryCode"
                    >
                      <template #selected-option-container="{ option }">
                        <div class="vs__selected">
                          {{ $t(option.label) }}
                        </div>
                      </template>

                      <template #option="{ label }">
                        {{ $t(label) }}
                      </template>
                    </v-select>

                    <b-form-invalid-feedback v-if="errors[0]">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: COURT -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_COURTS')"
                  label-for="court_id"
                >
                  <v-select
                    v-model="useUserData.address.court_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :disabled="useUserData.hide_address"
                    :options="courts"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="court_id"
                  />
                </b-form-group>
              </b-col>

            </b-row>
            <hr>
            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_GENERAL_INFORMATION') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                v-for="variable, index in useUserData.custom_variables_v2"
                :key="variable.custom_variable_id"
                cols="12"
                md="4"
              >
                <!-- <validation-provider
                  #default="{ errors }"
                  :name="$t(variable.customVarLabel)"
                  rules="required"
                >
                  <b-form-group
                    :label="$t(variable.customVarLabel)"
                    label-for="h-first-name"
                  >
                    <b-form-radio-group
                      v-if="variable.type === 'boolean'"
                      v-model="variable.customVarValue"
                      :options="radioOptions"
                      value-field="item"
                      text-field="name"
                      disabled-field="notEnabled"
                      class="demo-inline-spacing radiocustomvariables"
                    />
                    <b-form-input
                      v-else
                      id="h-first-name"
                      v-model="variable.customVarValue"
                      :placeholder="$t(variable.variable)"
                    />
                  </b-form-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider> -->
                <div v-if="variable.display_in_add_edit_page === 1 || variable.display_in_add_edit_page === true">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(variable.customVarLabel)"
                    :rules="getRules(variable.is_required, variable.type)"
                  >
                    <b-form-group
                      :label="$t(variable.customVarLabel)"
                      label-for="h-first-name"
                    >

                      <b-form-timepicker
                        v-if="variable.type === 'time'"
                        v-model="variable.customVarValue"
                        class="timepicker"
                      />
                      <b-input-group
                        v-else-if="variable.type === 'date'"
                      >
                        <cleave
                          id="dob"
                          v-model="debtorGeneralDates[`da${variable.customVarLabel}`]"
                          class="form-control"
                          :raw="false"
                          :options="options.date"
                          placeholder="DD-MM-YYYY"
                          @input="validateDate(0, debtorGeneralDates[`da${variable.customVarLabel}`], variable.customVarLabel, index)"
                        />
                        <b-input-group-append>
                          <b-form-datepicker
                            v-model="variable.customVarValue"
                            :date-format-options="{ day: 'numeric', month: 'numeric', year: 'numeric' }"
                            trim
                            button-only
                            right
                            size="sm"
                            @input="validateDate(1, variable.customVarValue, variable.customVarLabel, index)"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <b-form-textarea
                        v-else-if="variable.type === 'multiline string'"
                        id="textarea-rows"
                        v-model="variable.customVarValue"
                        :placeholder="$t(variable.variable)"
                        rows="3"
                      />
                      <v-select
                        v-else-if="variable.type === 'select'"
                        v-model="variable.customVarValue"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="variable.options.length > 0 ? variable.options : useUserData.airportOptions"
                        :reduce="val => val.value"
                        :clearable="false"
                        input-id="debtor-login-status"
                      />
                      <b-form-radio-group
                        v-else-if="variable.type === 'boolean'"
                        v-model="variable.customVarValue"
                        :options="radioOptions"
                        value-field="item"
                        text-field="name"
                        disabled-field="notEnabled"
                        class="demo-inline-spacing"
                      />
                      <b-form-input
                        v-else-if="variable.type === 'number'"
                        v-model="variable.customVarValue"
                        type="number"
                        :placeholder="$t(variable.variable)"
                      />
                      <b-form-input
                        v-else
                        id="h-first-name"
                        v-model="variable.customVarValue"
                        :placeholder="$t(variable.variable)"
                      />
                      <!-- <small
                      :id="'text-danger-'+variable.id"
                      class="noShow"
                    >Field {{ $t(variable.variable) }} is required.</small> -->
                      <small class="text-danger">{{ errors[0] }}</small>
                      <label
                        :id="'dateerror-'+variable.id"
                        style="color: red; display:none;"
                      >*{{ $t('DEBTIA_DATE_FORMAT_ERROR') }}</label>
                    </b-form-group>
                  </validation-provider>
                </div>
              </b-col>

            </b-row>
            <hr>

            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE_CHANGES') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1 employee_btn"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              @click="representativeClick"
            >
              {{ hasRepresentative ? $t('DEBTIA_REMOVE_REPRESENTATIVE') : $t('DEBTIA_ADD_REPRESENTATIVE') }}
            </b-button>
          </b-form>
        </validation-observer>
        <validation-observer
          #default="{ handleSubmit }"
          ref="refRepresentativeFormObserver"
        >
          <b-form
            @submit.prevent="handleSubmit(onRepresentativeSubmit)"
            @reset.prevent="resetForm"
          >
            <hr>
            <b-row
              v-if="boxOpen"
            >
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_REPRESENTATIVE') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="name"
                  >
                    <b-form-input
                      id="name"
                      v-model="representative.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('DEBTIA_COMPANY')"
                  label-for="company"
                >
                  <b-form-input
                    id="company"
                    v-model="representative.company"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('DEBTIA_PHONE')"
                  label-for="phone"
                >
                  <b-form-input
                    id="phone"
                    v-model="representative.phone"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="representative.email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-row
                class="debtor-address"
              >
                <b-col
                  cols="12"
                  md="12"
                ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

                <!-- Field: House Number -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    #default="validationContext"
                    name="House Number"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_HOUSE_NUMBER')"
                      label-for="house-number"
                    >
                      <b-form-input
                        id="house-number"
                        v-model="representative.address.house_number"
                        :state="getValidationState(validationContext)"
                        trim
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Field: House Number -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Road Name"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_ROAD_NAME')"
                      label-for="road-name"
                    >
                      <b-form-input
                        id="road-name"
                        v-model="representative.address.road_name"
                        :state="getValidationState(validationContext)"
                        trim
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Field: Place -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PLACE_NAME')"
                    label-for="place"
                  >
                    <b-form-input
                      id="place"
                      v-model="representative.address.place"
                      trim
                    />
                  </b-form-group>
                </b-col>

                <!-- Field: Zipcode -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Zip Code"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_ZIP_CODE')"
                      label-for="zip-code"
                    >
                      <b-form-input
                        id="zip-code"
                        v-model="representative.address.zip_code"
                        :state="getValidationState(validationContext)"
                        trim
                        @change="getCityNameRepresentative($event)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Field: City -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    #default="validationContext"
                    name="City"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_CITY')"
                      label-for="city"
                    >
                      <b-form-input
                        id="city"
                        v-model="representative.address.city"
                        :state="getValidationState(validationContext)"
                        trim
                        @change="getZipCodeRepresentative($event)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Field: Country -->
                <b-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    #default="validationContext"
                    name="Country"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_COUNTRY')"
                      label-for="country"
                    >
                      <v-select
                        v-model="representative.address.country_id"
                        :disabled="busy"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="countries"
                        :reduce="val => val.value"
                        :clearable="false"
                        input-id="select-country"
                        :value="'Select Country'"
                      >
                        <template
                          #selected-option-container="{ option }"
                        >
                          <div class="vs__selected">
                            {{ $t(option.label) }}</div>
                        </template>
                        <template
                          slot="option"
                          slot-scope="option"
                        >
                          {{ $t(option.label) }}
                        </template>
                      </v-select>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

              </b-row>
              <b-button
                variant="primary"
                class="mb-1 mb-sm-0 mr-0 mr-sm-1 debtor-save"
                :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                type="submit"
              >
                {{ hasRepresentative ? $t('DEBTIA_UPDATE') : $t('DEBTIA_ADD') }}
              </b-button>
              <hr>
            </b-row>
          </b-form>

        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BCard,
  BFormInvalidFeedback, BOverlay, BAlert, BFormRadioGroup, BFormDatepicker,
  BFormTimepicker, BFormTextarea, BInputGroupAppend, BInputGroup,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { ref/* , onUnmounted  */ } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'
import Cleave from 'vue-cleave-component'
import countries from '@/countries'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'

import formValidation from '@core/comp-functions/forms/form-validation'
import {
  required, alphaNum, email, datePattern,
} from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import i18n from '@/libs/i18n'
import clientStoreModule from '../debtorStoreModule'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    BAlert,
    Cleave,
    BFormRadioGroup,
    BFormDatepicker,
    BFormTimepicker,
    BFormTextarea,
    BInputGroupAppend,
    BInputGroup,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    clients: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      countries,
      required,
      datePattern,
      alphaNum,
      email,
      alertErrors: [],
      useUserData: { ...this.userData },
      validateUsername: true,
      radioOptions: [
        { item: 'true', name: 'True' },
        { item: 'false', name: 'False' },
      ],
      representative: {
        id: 0,
        debtor_id: this.$route.params.id,
        name: '',
        company: '',
        phone: '',
        email: '',
        address: {
          house_number: '',
          place: '',
          road_name: '',
          zip_code: '',
          city: '',
          country_id: 247,
        },
      },
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
        date: {
          date: true,
          delimiter: '-',
          datePattern: ['d', 'm', 'Y'],
        },
      },
      debtorsBelongToCompany: localStorage.getItem('debtorsBelongTo') === 'company',
      maxCharacters: 50,
      maxCharactersAllowed: 50,
      courts: [],
      debtorGeneralDates: {},
    }
  },
  created() {
    this.getRepresentative()
    this.getCourtsDropdown()
    this.maxCharacters = this.maxCharactersAllowed - this.useUserData.username.length
    this.useUserData.custom_variables_v2.forEach(element => {
      if (element.type === 'date') {
        // console.log('element is ', element)
        this.debtorGeneralDates[`da${element.customVarLabel}`] = this.$DAformattedDate(element.customVarValue)
      }
    })
  },
  setup(props) {
    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]
    const loader = false
    const hasRepresentative = false
    const boxOpen = false

    const refInputEl = ref(null)
    const previewEl = ref(null)

    const {
      refRepresentativeFormObserver,
      refFormObserver,
      getValidationState,
    } = formValidation(props.userData)

    const typeOptions = [
      { label: 'Person', value: 'person' },
      { label: 'Sole Proprietorship', value: 'sole-proprietorship' },
      { label: 'Capital Company', value: 'capital-company' },
    ]

    return {
      avatarText,
      loginOptions,
      loader,
      hasRepresentative,
      boxOpen,
      refInputEl,
      previewEl,
      refFormObserver,
      refRepresentativeFormObserver,
      getValidationState,
      BFormInvalidFeedback,
      typeOptions,
    }
  },
  methods: {
    getRules(isRequired, type) {
      let validationRules = ''
      if (isRequired === 1 && type === 'date') {
        validationRules = 'required|datePattern'
      }
      if (isRequired === 1 && type !== 'date') {
        validationRules = 'required'
      }
      if (isRequired !== 1 && type === 'date') {
        validationRules = 'datePattern'
      }
      return validationRules
    },
    validateDate(type, value, label, index) {
      if (type === 0) {
        this.useUserData.custom_variables_v2[index].customVarValue = this.$FormformattedDate(value)
      } else {
        this.debtorGeneralDates[`da${label}`] = this.$DAformattedDate(value)
      }
    },
    getCityNCourtName(event) {
      const countryId = this.useUserData.address.country_id
      if (countryId !== 247) return
      axios.get(`settings/get-city-court-by-code?code=${event}&country_id=${countryId}`)
        .then(response => {
          if (response.data.data.city_name !== null) {
            this.userData.address.city = response.data.data.city_name
            this.userData.address.court_id = response.data.data.court_id
          }
        }).catch(error => {
          console.log(error)
        })
    },
    async getCourtsDropdown() {
      await axios.get('settings/courts/dropdown')
        .then(response => {
          this.courts = [{ label: 'Select Court', value: 0 }, ...response.data.data]
        }).catch(() => false)
    },
    getZipCode(event) {
      const countryId = this.useUserData.address.country_id
      if (countryId !== 247) return
      axios.get(`settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          if (response.data.data.zip_code !== null) {
            this.userData.address.zip_code = response.data.data.zip_code
            this.getCityNCourtName((response.data.data.zip_code.length !== '') ? response.data.data.zip_code : 0)
          }
        }).catch(error => {
          console.log(error)
        })
    },
    getCityNameRepresentative(event) {
      axios.get(`settings/get-city-name-by-code?code=${event}`)
        .then(response => {
          if (response.data.data.city_name !== null) {
            this.representative.address.city = response.data.data.city_name
          }
        }).catch(error => {
          console.log(error)
        })
    },
    getZipCodeRepresentative(event) {
      axios.get(`settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          if (response.data.data.zip_code !== null) {
            this.representative.address.zip_code = response.data.data.zip_code
          }
        }).catch(error => {
          console.log(error)
        })
    },
    onRepresentativeSubmit() {
      let url = ''
      let msg = ''
      if (this.hasRepresentative) {
        url = '/representative/edit'
        msg = i18n.t('DEBTIA_REPRESENTATIVE_UPDATED')
      } else {
        url = '/representative/create'
        msg = i18n.t('DEBTIA_REPRESENTATIVE_ADDED')
      }
      this.loader = true
      axios
        .post(url, { ...this.representative })
        .then(response => {
          if (response.status) {
            this.hasRepresentative = true
            this.loader = false
            this.representative.id = response.data.data.id
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Message',
                icon: 'CoffeeIcon',
                variant: 'success',
                text: msg,
              },
            })
          }
        })
    },
    refreshRepresentative() {
      this.representative = {
        id: 0,
        debtor_id: this.$route.params.id,
        name: '',
        company: '',
        phone: '',
        email: '',
        address: {
          house_number: '',
          place: '',
          road_name: '',
          zip_code: '',
          city: '',
          country_id: 247,
        },
      }
    },
    async getRepresentative() {
      if (this.useUserData.representative) {
        this.hasRepresentative = true
        this.boxOpen = true
        this.representative.id = this.useUserData.representative.id
        this.representative.name = this.useUserData.representative.name
        this.representative.company = this.useUserData.representative.company
        this.representative.phone = this.useUserData.representative.phone
        this.representative.email = this.useUserData.representative.email
        this.representative.address.road_name = this.useUserData.representative.representative_address.road_name
        this.representative.address.house_number = this.useUserData.representative.representative_address.house_no
        this.representative.address.place = this.useUserData.representative.representative_address.place
        this.representative.address.zip_code = this.useUserData.representative.representative_address.zip_code
        this.representative.address.city = this.useUserData.representative.representative_address.city
        this.representative.address.country_id = this.useUserData.representative.representative_address.country_id
      }
    },
    representativeClick() {
      if (this.hasRepresentative) {
        this.confirmButtonColor()
      } else {
        this.boxOpen = true
      }
    },
    // comfirm button color
    confirmButtonColor() {
      this.$swal({
        title: i18n.t('DEBTIA_SURE_MSG'),
        text: i18n.t('DEBTIA_REVERT_MSG'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          const { id } = this.representative
          axios.post('/representative/delete', { id })
            .then(response => {
              this.boxOpen = false
              this.hasRepresentative = false
              this.refreshRepresentative()
              this.$refs.refRepresentativeFormObserver.reset()
              if (response.status) {
                this.$swal({
                  icon: 'success',
                  title: 'Deleted!',
                  text: i18n.t('DEBTIA_REPRESENTATIVE_DELETED'),
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                })
              }
            })
        }
      })
    },
    buildVariables() {
      return (this.useUserData.custom_variables_v2 || []).map(element => ({
        id: element.id,
        value: element.customVarValue,
        custom_variable_id: element.custom_variable_id,
      }))
    },
    EditDebtorPayload(variables) {
      const user = this.useUserData
      const address = user.address || {}
      const customVariables = user.custom_variables || []
      const debtCase = user.debt_case || []

      const payload = {
        id: user.id,
        name: user.name,
        client_id: user.client_id,
        user_id: user.user_id,
        bc_debtor_id: user.bc_debtor_id,
        debt_case_id: user.debt_case_id,
        debtor_type: user.debtor_type,
        username: user.username,
        phone: user.phone,
        email: user.email,
        care_of: user.care_of,
        contact_person: user.contact_person,
        birthdate: user.birthdate,
        cvr_number: user.cvr_number,
        cpr: user.cpr,
        state: user.state,
        added_by: user.added_by,
        bank_branch_no: user.bankBranchNo,
        bank_account_no: user.bankAccountNo,
        postcode_id: user.postcode_id,
        invoice_client_id: user.invoice_client_id,
        is_aks_registered: user.is_aks_registered,
        is_aks_error: user.is_aks_error,
        uuid: user.uuid,
        is_migrated: user.is_migrated,
        created_at: user.created_at,
        updated_at: user.updated_at,
        bc_data_updated: user.bc_data_updated,
        debbie_debtor_id: user.debbie_debtor_id,
        loginStatus: user.loginStatus,
        caseBirthdate: user.caseBirthdate,
        hide_address: user.hide_address,
        custom_variables: customVariables,
        debt_case: debtCase,
        bankBranchNo: user.bankBranchNo,
        bankAccountNo: user.bankAccountNo,
        type: user.debtor_type,
        cvrcpr: user.cvr_number,
        variables,
        address: {
          id: address.id,
          debtor_id: address.debtor_id,
          road_name: address.road_name,
          house_number: address.house_number,
          place: address.place,
          zip_code: address.zip_code,
          court_id: address.court_id,
          city: address.city,
          country: address.country,
          country_id: address.country_id,
          court: address.court,
        },
      }

      if (this.debtorsBelongToCompany) {
        payload.client_id = 0
      }

      return payload
    },
    async onSubmit() {
      this.loader = true
      const data = this.EditDebtorPayload(this.buildVariables())
      const validation = await axios.post('debtor/validate-username', { username: data.username, exclude: [data.id] })
        .then(() => true).catch(() => false)

      if (!validation) {
        this.validateUsername = validation
        this.loader = false
        return false
      }
      const USER_APP_STORE_MODULE_NAME = 'app-debtor'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, clientStoreModule)
      // console.log('--->', data)
      store.dispatch('app-debtor/editDebtor', data)
        .then(() => {
          // console.log('done', response)
          this.$router.replace(`/debtors/view/${data.id}`)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Debtor Updated',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Debtor successfully Updated!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors = []
          const erroMessage = error.data.message
          if (Array.isArray(erroMessage)) {
            this.alertErrors.push(...erroMessage)
          } else {
            this.alertErrors.push(erroMessage)
          }
          this.loader = false
        })
      return true
    },

    resetForm() {
      this.useUserData = { ...this.userData }
    },
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
    onCountryCode(val) {
      if (val) {
        this.useUserData.address.city = null
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}

.employee_btn{
  float:right;
}

.debtor-address{
  padding: 15px;
  border: 1px solid #d8d6de;
  border-radius: 10px;
  margin: 0px 15px 0px 15px;
}
.debtor-save{
  margin: 15px 15px 0px 18px;
}

.radiocustomvariables .custom-radio {
  margin-top: 0.75rem !important;
}
</style>
